/**
 * Counterbalance chains — the read side.
 *
 * A few pipelines are consecutive roles in ONE engine chain, where a later role
 * checks or builds on an earlier role's output. Where the design deliberately
 * puts a different model family on each side, pointing both roles at the same
 * one quietly removes the check: a model asked to audit its own work agrees
 * with itself, and nothing is ever rejected.
 *
 * The arrangement is DECLARED IN THE ADMIN BACKEND
 * (routes/pipelineModels.js → COUNTERBALANCE_CHAINS), not in the database and
 * not here — it is a property of how the engine is wired, and it arrives on
 * every pipeline row as the additive `counterbalance` field. This module only
 * reads that field: it folds the per-row memberships into the page-level list
 * and turns each verdict into a sentence an operator who has never read the
 * design doc can act on.
 *
 * It is a WARNING, never a block. Grant made exactly this change deliberately
 * on 2026-07-27; the system's job is to say so out loud, not to refuse.
 */

import { PipelineModel, PipelineCounterbalancePartner } from '../../../services/quillAdminService';

export interface CounterbalanceConflict {
  /** Stable identity for keys and dedup: chain + the two keys + the level. */
  id: string;
  chain_key: string;
  label: string;
  distinct: 'provider' | 'model';
  why: string;
  a: { pipeline_key: string; display_name: string; role: string | null; provider: string | null; model_id: string | null };
  b: { pipeline_key: string; display_name: string; role: string | null; provider: string | null; model_id: string | null };
}

/** 'provider' → "provider"; 'model' → "model". Used in every sentence below. */
export const distinctNoun = (distinct: 'provider' | 'model'): string =>
  distinct === 'provider' ? 'provider' : 'model';

/** "xai · grok-4.3" — the same one-identity rendering the summary line uses. */
export const refLabel = (ref: { provider: string | null; model_id: string | null }): string => {
  if (!ref.model_id) return '— none —';
  return ref.provider ? `${ref.provider} · ${ref.model_id}` : ref.model_id;
};

/** What both sides now share — the thing that collapsed the arrangement. */
export const sharedValue = (
  conflict: Pick<CounterbalanceConflict, 'distinct' | 'a'>
): string => (conflict.distinct === 'provider' ? conflict.a.provider ?? '' : conflict.a.model_id ?? '');

/**
 * The one-line headline for a collapsed pairing. Names the two roles, the level
 * they were meant to differ at, and what they now share — no jargon, no codes.
 */
export function conflictHeadline(conflict: CounterbalanceConflict): string {
  return (
    `${conflict.a.display_name} and ${conflict.b.display_name} are both running on `
    + `the same ${distinctNoun(conflict.distinct)} (${sharedValue(conflict)}).`
  );
}

/**
 * The healthy-state sentence for one partner pairing — shown so an operator
 * sees the arrangement BEFORE they break it, which is the whole point.
 */
export function partnerSummary(partner: PipelineCounterbalancePartner): string {
  const noun = distinctNoun(partner.distinct);
  return partner.collapsed
    ? `Both are on the same ${noun} right now (${refLabel(partner.other_model)}).`
    : `Kept on a different ${noun} on purpose — that one runs on ${refLabel(partner.other_model)}.`;
}

/**
 * Fold every row's memberships into the page-level conflict list.
 *
 * A collapsed pairing is reported by BOTH of its rows, so pairings are deduped
 * on chain + the two keys + the level. Order follows the pipeline list, so the
 * banner reads in the same order as the board underneath it.
 */
export function deriveCounterbalanceConflicts(pipelines: PipelineModel[]): CounterbalanceConflict[] {
  const seen = new Set<string>();
  const conflicts: CounterbalanceConflict[] = [];

  for (const pipeline of pipelines) {
    for (const membership of pipeline.counterbalance ?? []) {
      for (const partner of membership.partners) {
        if (!partner.collapsed) continue;
        const pair = [pipeline.pipeline_key, partner.pipeline_key].sort();
        const id = `${membership.chain_key}|${pair[0]}|${pair[1]}|${partner.distinct}`;
        if (seen.has(id)) continue;
        seen.add(id);
        conflicts.push({
          id,
          chain_key: membership.chain_key,
          label: membership.label,
          distinct: partner.distinct,
          why: partner.why,
          a: {
            pipeline_key: pipeline.pipeline_key,
            display_name: pipeline.display_name,
            role: membership.role,
            provider: partner.this_model.provider,
            model_id: partner.this_model.model_id,
          },
          b: {
            pipeline_key: partner.pipeline_key,
            display_name: partner.display_name,
            role: partner.role,
            provider: partner.other_model.provider,
            model_id: partner.other_model.model_id,
          },
        });
      }
    }
  }

  return conflicts;
}

/** True when this row is on at least one collapsed pairing. */
export function pipelineHasCollapsedCounterbalance(pipeline: PipelineModel): boolean {
  return (pipeline.counterbalance ?? []).some((m) => m.collapsed);
}
